/**
 * ADR-0210 — real `AgentRollupsAPI` backed by the HTTP client.
 *
 *   get: GET /v1/sites/:site_id/agent-rollups?range=&agent_key=
 *
 * Errors propagate as `ApiError` (the view renders an inline error state);
 * an empty body is treated as an empty window rather than a failure.
 */

import type { SiteId } from '@sill/types';
import type { AgentDemandResponse, AgentInsightsResponse, AgentKind, AgentOverviewResponse, AgentRollupRange, AgentRollupsAPI, AgentRollupsResponse } from '@sill/types/api';

import type { HttpClient } from './http-client.js';

export function createRealAgentRollupsApi(deps: { readonly http: HttpClient }): AgentRollupsAPI {
  return {
    async get(site_id: SiteId, range: AgentRollupRange, agent_key?: string, kind: AgentKind = 'all'): Promise<AgentRollupsResponse> {
      const qs = new URLSearchParams({ range, kind });
      if (agent_key !== undefined) qs.set('agent_key', agent_key);
      const res = await deps.http.request<AgentRollupsResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/agent-rollups?${qs.toString()}`,
      });
      return res ?? { derived: true, range, window: { from_day: '', to_day: '' }, agents: [] };
    },
    async demand(site_id: SiteId, range: AgentRollupRange, kind: AgentKind = 'all'): Promise<AgentDemandResponse> {
      const res = await deps.http.request<AgentDemandResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/agent-demand?range=${range}&kind=${kind}`,
      });
      return res ?? {
        derived: true, range, window: { from_day: '', to_day: '' }, has_catalog: false,
        products: [], refused: [], unavailable: [], searched_unsold: [], searched_all: [], top_pages: [], pages_by_kind: {}, est_value_note: '',
      };
    },
    async overview(site_id: SiteId, range: AgentRollupRange, kind: AgentKind = 'all'): Promise<AgentOverviewResponse> {
      const res = await deps.http.request<AgentOverviewResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/agent-overview?range=${range}&kind=${kind}`,
      });
      const zero = { traffic: 0, agents: 0, sessions: 0, actions: 0, approved: 0, rejected: 0, revenue_minor: 0, currency: null };
      return res ?? {
        derived: true, range, window: { from_day: '', to_day: '' }, prior_window: { from_day: '', to_day: '' },
        totals: zero, prior: zero, series: { days: [], agents: [], other: [] }, categories: {},
        funnel: { sessions: 0, product: 0, search: 0, cart: 0, checkout: 0, actions: 0, approved: 0, find_agents: 0, discovery_hits: 0, discovery_by_kind: {}, by_agent: [] }, refusals_by_day: [], identity: { known: 0, unregistered: 0, no_identity: 0, impersonation: 0, human: 0 }, heatmap: [], mix: { days: [], series: { ai: [], bots: [], humans: [] }, totals: { ai: { traffic: 0, prior_traffic: 0, agents: 0 }, bots: { traffic: 0, prior_traffic: 0, agents: 0 }, humans: { traffic: 0, prior_traffic: 0, agents: 0 } } },
      };
    },
    async insights(site_id: SiteId, days: 7 | 30 = 7): Promise<AgentInsightsResponse> {
      const res = await deps.http.request<AgentInsightsResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/agent-insights?days=${days}`,
      });
      return res ?? { derived: true, insights: [] };
    },
    async dismissInsight(site_id: SiteId, insight_id: string): Promise<void> {
      await deps.http.request<void>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(site_id)}/agent-insights/${encodeURIComponent(insight_id)}/dismiss`,
      });
    },
  };
}
